import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/lib/auth/AuthContext";
import {
  fetchCatalog,
  type CatalogNotificationChannel,
  type CatalogPolicy,
  type CatalogRequestType,
  type CatalogSnapshot,
  type CatalogVoiceAgentBinding,
} from "@/lib/catalog-api";
import {
  managementTenantKey,
  setStaffTenantFallback,
} from "@/lib/management-api";
import type { DraftPolicy } from "../ConfigurationApp";
import type { PendingCoveragePath } from "../CoveragePath/CoveragePath";
import { ConfigurationContext } from "./context/ConfigurationContext";
import {
  clone,
  defaultsSignature,
  emptyPolicy,
  policySignature,
  policyToDraft,
  preferredPolicy,
  requestSignature,
  type DefaultsSnapshot,
} from "./lib/drafts";

function selectPolicyDraft(
  current: DraftPolicy,
  policy: CatalogPolicy,
  patch?: { displayName: string; isActive: boolean },
  cached?: DraftPolicy,
): DraftPolicy {
  const same =
    !current.pendingKey &&
    current.escalationPolicyId != null &&
    current.escalationPolicyId === policy.escalationPolicyId;
  const base = same ? current : (cached ?? policyToDraft(policy));
  if (!patch) return base;
  return {
    ...base,
    displayName: patch.displayName,
    isActive: patch.isActive,
  };
}

export function ConfigurationProvider({ children }: { children: ReactNode }) {
  const { userProfile, isLoading: authLoading } = useAuth();
  const tenantKey =
    managementTenantKey() || userProfile?.profile.tenantKey?.trim() || "";
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [catalog, setCatalog] = useState<CatalogSnapshot | null>(null);
  const [mode, setMode] = useState<"hub" | "wizard">("hub");
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [revision, setRevision] = useState(0);

  const [channels, setChannels] = useState<CatalogNotificationChannel[]>([]);
  const [policyDraft, setPolicyDraft] = useState<DraftPolicy>(emptyPolicy());
  const [pendingPaths, setPendingPaths] = useState<PendingCoveragePath[]>([]);
  const [dismissedPolicyIds, setDismissedPolicyIds] = useState<number[]>([]);
  const catalogSnapshotRef = useRef<DraftPolicy | null>(null);
  const catalogDraftsRef = useRef<Record<number, DraftPolicy>>({});
  const policyBaselinesRef = useRef<Record<number, string>>({});

  const [requestTypes, setRequestTypes] = useState<CatalogRequestType[]>([]);
  const requestBaselineRef = useRef<CatalogRequestType[]>([]);

  const [wakePhrase, setWakePhrase] = useState("");
  const [wakeOnnx, setWakeOnnx] = useState("");
  const [voiceAgentId, setVoiceAgentId] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [updateWebhook, setUpdateWebhook] = useState(false);
  const [defaultPolicyId, setDefaultPolicyId] = useState<number | "">("");
  const [configEnabled, setConfigEnabled] = useState(true);
  const [hasWebhookSecret, setHasWebhookSecret] = useState(false);
  const defaultsBaselineRef = useRef<DefaultsSnapshot>({
    wakePhrase: "",
    wakeOnnx: "",
    voiceAgentId: "",
    webhookSecret: "",
    updateWebhook: false,
    defaultPolicyId: "",
    configEnabled: true,
  });
  const baselineDefaultPolicyIdRef = useRef<number | null>(null);

  const [voiceBindings, setVoiceBindings] = useState<CatalogVoiceAgentBinding[]>(
    [],
  );

  const bump = useCallback(() => setRevision((value) => value + 1), []);

  const defaultsSnapshot = useCallback((): DefaultsSnapshot => {
    return {
      wakePhrase,
      wakeOnnx,
      voiceAgentId,
      webhookSecret,
      updateWebhook,
      defaultPolicyId,
      configEnabled,
    };
  }, [
    wakePhrase,
    wakeOnnx,
    voiceAgentId,
    webhookSecret,
    updateWebhook,
    defaultPolicyId,
    configEnabled,
  ]);

  const applyDefaults = useCallback((snapshot: DefaultsSnapshot) => {
    setWakePhrase(snapshot.wakePhrase);
    setWakeOnnx(snapshot.wakeOnnx);
    setVoiceAgentId(snapshot.voiceAgentId);
    setWebhookSecret(snapshot.webhookSecret);
    setUpdateWebhook(snapshot.updateWebhook);
    setDefaultPolicyId(snapshot.defaultPolicyId);
    setConfigEnabled(snapshot.configEnabled);
  }, []);

  const hydrate = useCallback(
    (snap: CatalogSnapshot) => {
      setCatalog(snap);
      setChannels(snap.notificationChannels);
      setPendingPaths([]);
      setDismissedPolicyIds([]);
      catalogSnapshotRef.current = null;
      catalogDraftsRef.current = {};
      const baselines: Record<number, string> = {};
      for (const policy of snap.policies) {
        baselines[policy.escalationPolicyId] = policySignature(
          policyToDraft(policy),
        );
      }
      policyBaselinesRef.current = baselines;
      const preferred = preferredPolicy(snap.policies);
      setPolicyDraft(preferred ? policyToDraft(preferred) : emptyPolicy());
      setRequestTypes(snap.requestTypes);
      requestBaselineRef.current = clone(snap.requestTypes);
      const snapshot: DefaultsSnapshot = {
        wakePhrase: snap.configuration?.wakePhrase ?? "",
        wakeOnnx: snap.configuration?.wakeWordOnnxPath ?? "",
        voiceAgentId: snap.configuration?.voiceAgentId ?? "",
        webhookSecret: "",
        updateWebhook: false,
        defaultPolicyId: snap.configuration?.defaultEscalationPolicyId ?? "",
        configEnabled: snap.configuration?.isEnabled ?? true,
      };
      defaultsBaselineRef.current = snapshot;
      baselineDefaultPolicyIdRef.current =
        snap.configuration?.defaultEscalationPolicyId ?? null;
      applyDefaults(snapshot);
      setHasWebhookSecret(Boolean(snap.configuration?.hasWebhookSecret));
      setVoiceBindings(snap.voiceAgentBindings ?? []);
      bump();
    },
    [applyDefaults, bump],
  );

  const reload = useCallback(async () => {
    if (authLoading) return;
    setLoading(true);
    setLoadError(null);
    setStaffTenantFallback(userProfile?.profile.tenantKey);
    if (!tenantKey) {
      setLoading(false);
      setUnavailable(true);
      return;
    }
    const result = await fetchCatalog();
    setLoading(false);
    if (!result.ok || !result.catalog) {
      if (result.unavailable) {
        setUnavailable(true);
        return;
      }
      setLoadError(result.message || "Unable to load catalog");
      return;
    }
    setUnavailable(false);
    hydrate(result.catalog);
  }, [authLoading, hydrate, tenantKey, userProfile?.profile.tenantKey]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!policyDraft.pendingKey) return;
    setPendingPaths((prev) => {
      const row = prev.find((item) => item.key === policyDraft.pendingKey);
      if (!row || row.draft === policyDraft) return prev;
      return prev.map((item) =>
        item.key === policyDraft.pendingKey
          ? { ...item, draft: policyDraft }
          : item,
      );
    });
  }, [policyDraft]);

  if (
    !policyDraft.pendingKey &&
    policyDraft.escalationPolicyId &&
    !dismissedPolicyIds.includes(policyDraft.escalationPolicyId)
  ) {
    catalogDraftsRef.current[policyDraft.escalationPolicyId] = policyDraft;
    catalogSnapshotRef.current = policyDraft;
  }

  const visiblePolicies = useMemo(
    () =>
      (catalog?.policies ?? []).filter(
        (policy) => !dismissedPolicyIds.includes(policy.escalationPolicyId),
      ),
    [catalog?.policies, dismissedPolicyIds],
  );

  const createNewDraft = useCallback((pendingKey: string): DraftPolicy => {
    return {
      ...emptyPolicy(),
      pendingKey,
      displayName: "",
      code: "",
    };
  }, []);

  const adoptVisiblePendingPath = useCallback(() => {
    const key = `new-${Date.now()}-${pendingPaths.length}`;
    const draft = createNewDraft(key);
    setPendingPaths([{ key, draft }]);
    setPolicyDraft(draft);
  }, [createNewDraft, pendingPaths.length]);

  const restoreCatalogDraft = useCallback(() => {
    const snapshot = catalogSnapshotRef.current;
    if (
      snapshot?.escalationPolicyId &&
      !dismissedPolicyIds.includes(snapshot.escalationPolicyId)
    ) {
      setPolicyDraft(snapshot);
      return;
    }
    const fallback = preferredPolicy(visiblePolicies);
    if (fallback) {
      setPolicyDraft(
        catalogDraftsRef.current[fallback.escalationPolicyId] ??
          policyToDraft(fallback),
      );
      return;
    }
    adoptVisiblePendingPath();
  }, [adoptVisiblePendingPath, dismissedPolicyIds, visiblePolicies]);

  const selectPolicy = useCallback(
    (
      policy: CatalogPolicy,
      patch?: { displayName: string; isActive: boolean },
    ) => {
      if (policyDraft.pendingKey) {
        setPendingPaths((prev) =>
          prev.map((item) =>
            item.key === policyDraft.pendingKey
              ? { ...item, draft: policyDraft }
              : item,
          ),
        );
      }
      setPolicyDraft((prev) => {
        if (!prev.pendingKey && prev.escalationPolicyId) {
          catalogDraftsRef.current[prev.escalationPolicyId] = prev;
        }
        return selectPolicyDraft(
          prev,
          policy,
          patch,
          catalogDraftsRef.current[policy.escalationPolicyId],
        );
      });
    },
    [policyDraft],
  );

  const rememberPolicy = useCallback(
    (
      policy: CatalogPolicy,
      values: { displayName: string; isActive: boolean },
    ) => {
      const id = policy.escalationPolicyId;
      const cached = catalogDraftsRef.current[id] ?? policyToDraft(policy);
      catalogDraftsRef.current[id] = {
        ...cached,
        displayName: values.displayName,
        isActive: values.isActive,
      };
      bump();
    },
    [bump],
  );

  const policyWithEdits = useCallback((policy: CatalogPolicy): CatalogPolicy => {
    const cached = catalogDraftsRef.current[policy.escalationPolicyId];
    if (!cached) return policy;
    return {
      ...policy,
      displayName: cached.displayName,
      isActive: cached.isActive,
      code: cached.code,
    };
  }, []);

  const upsertChannel = useCallback((channel: CatalogNotificationChannel) => {
    setChannels((prev) => {
      const index = prev.findIndex(
        (item) =>
          item.notificationChannelId === channel.notificationChannelId ||
          item.channelCode.toUpperCase() === channel.channelCode.toUpperCase(),
      );
      const next =
        index < 0
          ? [...prev, channel]
          : prev.map((item, itemIndex) =>
              itemIndex === index ? channel : item,
            );
      setCatalog((current) =>
        current ? { ...current, notificationChannels: next } : current,
      );
      return next;
    });
  }, []);

  const commitSavedPolicy = useCallback(
    (policy: CatalogPolicy) => {
      const draft = policyToDraft(policy);
      policyBaselinesRef.current[policy.escalationPolicyId] =
        policySignature(draft);
      catalogDraftsRef.current[policy.escalationPolicyId] = draft;
      setCatalog((current) => {
        if (!current) return current;
        const exists = current.policies.some(
          (item) => item.escalationPolicyId === policy.escalationPolicyId,
        );
        return {
          ...current,
          policies: exists
            ? current.policies.map((item) =>
                item.escalationPolicyId === policy.escalationPolicyId
                  ? policy
                  : item,
              )
            : [...current.policies, policy],
        };
      });
      setDismissedPolicyIds((prev) =>
        prev.filter((id) => id !== policy.escalationPolicyId),
      );
      setPendingPaths((prev) =>
        policyDraft.pendingKey
          ? prev.filter((item) => item.key !== policyDraft.pendingKey)
          : prev,
      );
      setPolicyDraft(draft);
      bump();
    },
    [bump, policyDraft.pendingKey],
  );

  const commitPolicyActive = useCallback(
    (policyId: number, isActive: boolean) => {
      const cached = catalogDraftsRef.current[policyId];
      if (cached) {
        catalogDraftsRef.current[policyId] = { ...cached, isActive };
      }
      // Read the policy from the latest catalog inside the updater so an
      // overlapping commitSavedPolicy cannot leave a stale baseline.
      setCatalog((current) => {
        if (!current) return current;
        const existing = current.policies.find(
          (item) => item.escalationPolicyId === policyId,
        );
        if (existing) {
          policyBaselinesRef.current[policyId] = policySignature(
            policyToDraft({ ...existing, isActive }),
          );
        }
        return {
          ...current,
          policies: current.policies.map((item) =>
            item.escalationPolicyId === policyId
              ? { ...item, isActive }
              : item,
          ),
        };
      });
      setPolicyDraft((prev) =>
        prev.escalationPolicyId === policyId
          ? { ...prev, isActive }
          : prev,
      );
      bump();
    },
    [bump],
  );

  const discardPolicyEdits = useCallback(() => {
    catalogDraftsRef.current = {};
    catalogSnapshotRef.current = null;
    setPendingPaths([]);
    setDismissedPolicyIds([]);
    const policies = catalog?.policies ?? [];
    for (const policy of policies) {
      policyBaselinesRef.current[policy.escalationPolicyId] = policySignature(
        policyToDraft(policy),
      );
    }
    const preferred = preferredPolicy(policies);
    setPolicyDraft(preferred ? policyToDraft(preferred) : emptyPolicy());
    bump();
  }, [bump, catalog?.policies]);

  const markRequestsSaved = useCallback(
    (next: CatalogRequestType[], count: number, complete: boolean) => {
      requestBaselineRef.current = complete
        ? clone(next)
        : requestBaselineRef.current.map((item, index) =>
            index < count ? clone(next[index] ?? item) : item,
          );
      setRequestTypes(next);
      bump();
    },
    [bump],
  );

  const discardRequestEdits = useCallback(() => {
    setRequestTypes(clone(requestBaselineRef.current));
    bump();
  }, [bump]);

  const commitSavedDefaults = useCallback(
    (
      snapshot: DefaultsSnapshot,
      savedDefaultPolicyId: number | null,
      secretUpdated: boolean,
    ) => {
      defaultsBaselineRef.current = snapshot;
      baselineDefaultPolicyIdRef.current = savedDefaultPolicyId;
      applyDefaults(snapshot);
      if (secretUpdated) setHasWebhookSecret(true);
      setCatalog((current) => {
        if (!current?.configuration) return current;
        return {
          ...current,
          configuration: {
            ...current.configuration,
            wakePhrase: snapshot.wakePhrase,
            wakeWordOnnxPath: snapshot.wakeOnnx || null,
            voiceAgentId: snapshot.voiceAgentId || null,
            defaultEscalationPolicyId: savedDefaultPolicyId,
            isEnabled: snapshot.configEnabled,
            hasWebhookSecret:
              current.configuration.hasWebhookSecret || secretUpdated,
          },
        };
      });
      bump();
    },
    [applyDefaults, bump],
  );

  const noteSavedVoiceAgentId = useCallback(
    (nextVoiceAgentId: string) => {
      setVoiceAgentId(nextVoiceAgentId);
      defaultsBaselineRef.current = {
        ...defaultsBaselineRef.current,
        voiceAgentId: nextVoiceAgentId,
      };
      bump();
    },
    [bump],
  );

  const discardDefaultEdits = useCallback(() => {
    applyDefaults(defaultsBaselineRef.current);
    bump();
  }, [applyDefaults, bump]);

  const openWizard = useCallback((index: number) => {
    setMode("wizard");
    setStepIndex(index);
  }, []);

  const openPolicy = useCallback((policy: CatalogPolicy) => {
    setPolicyDraft(
      catalogDraftsRef.current[policy.escalationPolicyId] ??
        policyToDraft(policy),
    );
    setMode("wizard");
    setStepIndex(1);
  }, []);

  const policyDirty = useMemo(() => {
    // `revision` reruns this after ref-only updates such as remembering another path.
    void revision;
    if (pendingPaths.length > 0 || dismissedPolicyIds.length > 0) return true;
    const drafts = catalogDraftsRef.current;
    for (const [id, draft] of Object.entries(drafts)) {
      const baseline = policyBaselinesRef.current[Number(id)];
      if (baseline !== undefined && policySignature(draft) !== baseline) {
        return true;
      }
    }
    if (policyDraft.pendingKey) return true;
    if (policyDraft.escalationPolicyId) {
      const baseline =
        policyBaselinesRef.current[policyDraft.escalationPolicyId];
      if (
        baseline !== undefined &&
        policySignature(policyDraft) !== baseline
      ) {
        return true;
      }
    }
    return false;
  }, [dismissedPolicyIds.length, pendingPaths.length, policyDraft, revision]);

  const requestsDirty =
    requestSignature(requestTypes) !==
    requestSignature(requestBaselineRef.current);

  const defaultsDirty =
    defaultsSignature(defaultsSnapshot()) !==
    defaultsSignature(defaultsBaselineRef.current);

  const value = {
    loading: loading || authLoading,
    loadError,
    unavailable,
    catalog,
    reload,
    mode,
    setMode,
    stepIndex,
    setStepIndex,
    openWizard,
    saving,
    setSaving,
    channels,
    upsertChannel,
    policyDraft,
    setPolicyDraft,
    pendingPaths,
    setPendingPaths,
    dismissedPolicyIds,
    visiblePolicies,
    selectPolicy,
    rememberPolicy,
    restoreCatalogDraft,
    createNewDraft,
    policyWithEdits,
    openPolicy,
    commitSavedPolicy,
    commitPolicyActive,
    discardPolicyEdits,
    policyDirty,
    requestTypes,
    setRequestTypes,
    markRequestsSaved,
    discardRequestEdits,
    requestsDirty,
    wakePhrase,
    setWakePhrase,
    wakeOnnx,
    setWakeOnnx,
    voiceAgentId,
    setVoiceAgentId,
    webhookSecret,
    setWebhookSecret,
    updateWebhook,
    setUpdateWebhook,
    defaultPolicyId,
    setDefaultPolicyId,
    configEnabled,
    setConfigEnabled,
    hasWebhookSecret,
    baselineDefaultPolicyId: baselineDefaultPolicyIdRef.current,
    defaultsSnapshot,
    commitSavedDefaults,
    noteSavedVoiceAgentId,
    discardDefaultEdits,
    defaultsDirty,
    voiceBindings,
    setVoiceBindings,
  };

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
}
